// src/features/tuition/components/online/SubjectDetails.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Breadcrumbs,
  Link,
  Menu,
  MenuItem,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  MenuBook as MenuBookIcon,
  Topic as TopicIcon,
  SubdirectoryArrowRight as SubtopicIcon,
  BookmarkBorder as PrerequisiteIcon,
  Extension as SkillIcon,
  Link as LinkIcon,
  Description as DescriptionIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';
import { 
  fetchSubjectDetails, 
  fetchTopicsForSubject, 
  fetchSubtopicsForTopic,
  markTopicAsCompleted,
  markSubtopicAsCompleted
} from '../../services/subjectsService';
import TopicForm from './TopicForm';
import SubtopicForm from './SubtopicForm';

const SubjectDetails = ({ subjectId, userId, onBack, onEditTopic, onEditSubtopic }) => {
  const [subject, setSubject] = useState(null);
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedTopic, setExpandedTopic] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [contextMenuType, setContextMenuType] = useState(null); // 'topic' or 'subtopic'
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [isTopicFormOpen, setIsTopicFormOpen] = useState(false);
  const [isSubtopicFormOpen, setIsSubtopicFormOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedSubtopic, setSelectedSubtopic] = useState(null);
  
  useEffect(() => {
    loadSubjectDetails();
  }, [subjectId]);
  
  const loadSubjectDetails = async () => {
    setLoading(true);
    try {
      // Fetch subject details
      const subjectData = await fetchSubjectDetails(subjectId);
      setSubject(subjectData);
      
      // Fetch topics for the subject
      const topicsData = await fetchTopicsForSubject(subjectId);
      setTopics(topicsData);
      
      // Initialize subtopics object
      const subtopicsObj = {};
      
      // Fetch subtopics for each topic
      for (const topic of topicsData) {
        const subtopicsData = await fetchSubtopicsForTopic(topic.id);
        subtopicsObj[topic.id] = subtopicsData;
      }
      
      setSubtopics(subtopicsObj);
    } catch (error) {
      console.error("Error loading subject details:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleTopicClick = (topicId) => {
    if (expandedTopic === topicId) {
      setExpandedTopic(null);
    } else {
      setExpandedTopic(topicId);
    }
  };
  
  const handleMenuOpen = (event, itemType, itemId) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setContextMenuType(itemType);
    setSelectedItemId(itemId);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setContextMenuType(null);
    setSelectedItemId(null);
  };
  
  const handleEditTopic = () => {
    const topic = topics.find(t => t.id === selectedItemId);
    if (onEditTopic && topic) {
      onEditTopic(topic);
    } else {
      setSelectedTopic(topic);
      setIsTopicFormOpen(true);
    }
    handleMenuClose();
  };
  
  const handleEditSubtopic = () => {
    let selectedSubtopicData = null;
    let topicData = null;
    
    // Find the subtopic in the correct topic
    for (const topicId in subtopics) {
      const foundSubtopic = subtopics[topicId].find(s => s.id === selectedItemId);
      if (foundSubtopic) {
        selectedSubtopicData = foundSubtopic;
        topicData = topics.find(t => t.id === topicId);
        break;
      }
    }
    
    if (selectedSubtopicData) {
      if (onEditSubtopic) {
        onEditSubtopic(selectedSubtopicData, topicData);
      } else {
        setSelectedSubtopic(selectedSubtopicData);
        setSelectedTopic(topicData);
        setIsSubtopicFormOpen(true);
      }
    }
    
    handleMenuClose();
  };
  
  const handleAddTopic = () => {
    setSelectedTopic(null);
    setIsTopicFormOpen(true);
  };
  
  const handleAddSubtopic = (topicId) => {
    const topic = topics.find(t => t.id === topicId);
    setSelectedTopic(topic);
    setSelectedSubtopic(null);
    setIsSubtopicFormOpen(true);
  };
  
  const handleTopicFormClose = () => {
    setIsTopicFormOpen(false);
    setSelectedTopic(null);
  };
  
  const handleSubtopicFormClose = () => {
    setIsSubtopicFormOpen(false);
    setSelectedTopic(null);
    setSelectedSubtopic(null);
  };
  
  const handleTopicFormSubmit = async (topicData) => {
    // Save the topic data
    // This would be implemented in the TopicForm component
    handleTopicFormClose();
    await loadSubjectDetails(); // Reload data
  };
  
  const handleSubtopicFormSubmit = async (subtopicData) => {
    // Save the subtopic data
    // This would be implemented in the SubtopicForm component
    handleSubtopicFormClose();
    await loadSubjectDetails(); // Reload data
  };
  
  const handleMarkTopicCompleted = async (topicId) => {
    try {
      await markTopicAsCompleted(topicId);
      
      // Update local state
      setTopics(prevTopics => 
        prevTopics.map(topic => 
          topic.id === topicId ? { ...topic, completed: true } : topic
        )
      );
      
      // Also update the subject progress
      const completedTopics = topics.filter(t => t.id === topicId || t.completed).length;
      const totalTopics = topics.length;
      const progress = Math.round((completedTopics / totalTopics) * 100);
      
      setSubject(prevSubject => ({
        ...prevSubject,
        completed: completedTopics,
        progress
      }));
    } catch (error) {
      console.error("Error marking topic as completed:", error);
    }
  };
  
  const handleMarkSubtopicCompleted = async (topicId, subtopicId) => {
    try {
      await markSubtopicAsCompleted(subtopicId);
      
      // Update local state
      setSubtopics(prevSubtopics => ({
        ...prevSubtopics,
        [topicId]: prevSubtopics[topicId].map(subtopic => 
          subtopic.id === subtopicId ? { ...subtopic, completed: true } : subtopic
        )
      }));
      
      // Check if all subtopics of this topic are completed
      const updatedSubtopics = {
        ...subtopics,
        [topicId]: subtopics[topicId].map(subtopic => 
          subtopic.id === subtopicId ? { ...subtopic, completed: true } : subtopic
        )
      };
      
      const allCompleted = updatedSubtopics[topicId].every(subtopic => subtopic.completed);
      
      if (allCompleted) {
        // Mark the topic as completed too
        await handleMarkTopicCompleted(topicId);
      }
    } catch (error) {
      console.error("Error marking subtopic as completed:", error);
    }
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress sx={{ color: THEME_COLORS.primary }} />
      </Box>
    );
  }
  
  if (!subject) {
    return (
      <Box textAlign="center" p={4}>
        <Typography color="error">Subject not found</Typography>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={onBack}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }
  
  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        {/* Header with back button */}
        <Box display="flex" alignItems="center" mb={2}>
          <IconButton onClick={onBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Breadcrumbs aria-label="breadcrumb">
            <Link
              color="inherit"
              sx={{ cursor: 'pointer' }}
              onClick={onBack}
            >
              Subjects
            </Link>
            <Typography color="text.primary">{subject.name}</Typography>
          </Breadcrumbs>
        </Box>
        
        {/* Subject Header */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={9}>
            <Box display="flex" alignItems="flex-start">
              {subject.image && (
                <Box
                  component="img"
                  src={subject.image}
                  alt={subject.name}
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: 2,
                    objectFit: 'cover',
                    mr: 3
                  }}
                />
              )}
              <Box>
                <Box display="flex" alignItems="center" mb={1}>
                  <Typography variant="h5" fontWeight="bold">
                    {subject.name}
                  </Typography>
                  <Chip 
                    label={`Grade ${subject.grade}`} 
                    size="small"
                    sx={{ ml: 2, bgcolor: 'rgba(39, 35, 97, 0.08)' }}
                  />
                </Box>
                
                <Typography variant="body1" paragraph>
                  {subject.description}
                </Typography>
                
                <Box display="flex" alignItems="center">
                  <Chip 
                    icon={<MenuBookIcon />}
                    label={subject.department} 
                    variant="outlined"
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {topics.length} topics • {Object.values(subtopics).flat().length} subtopics
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Progress
                </Typography>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  {subject.progress}%
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={subject.progress} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 5,
                    mb: 1
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {subject.completed}/{topics.length} topics completed
                </Typography>
              </CardContent>
            </Card>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              fullWidth
              onClick={handleAddTopic}
              sx={{
                bgcolor: THEME_COLORS.primary,
                '&:hover': { bgcolor: '#1e1c4e' }
              }}
            >
              Add Topic
            </Button>
          </Grid>
        </Grid>
        
        <Divider sx={{ mb: 3 }} />
        
        {/* Topics List */}
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Topics
        </Typography>
        
        {topics.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary" paragraph>
              No topics have been added to this subject yet.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddTopic}
              sx={{
                bgcolor: THEME_COLORS.primary,
                '&:hover': { bgcolor: '#1e1c4e' }
              }}
            >
              Add First Topic
            </Button>
          </Box>
        ) : (
          topics.map((topic) => (
            <Accordion 
              key={topic.id} 
              expanded={expandedTopic === topic.id}
              onChange={() => handleTopicClick(topic.id)}
              sx={{ 
                mb: 2, 
                borderRadius: '8px !important',
                overflow: 'hidden',
                '&:before': {
                  display: 'none',
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: topic.completed ? 'rgba(76, 175, 80, 0.08)' : 'rgba(0, 0, 0, 0.02)',
                  borderLeft: '4px solid',
                  borderColor: topic.completed ? '#4CAF50' : THEME_COLORS.primary
                }}
              >
                <Grid container alignItems="center">
                  <Grid item xs={7} sm={9}>
                    <Box display="flex" alignItems="center">
                      <TopicIcon 
                        sx={{ 
                          mr: 1.5,
                          color: topic.completed ? '#4CAF50' : THEME_COLORS.primary
                        }}
                      />
                      <Typography variant="subtitle1" fontWeight="medium">
                        {topic.name}
                      </Typography>
                      {topic.completed && (
                        <Chip 
                          icon={<CheckIcon />}
                          label="Completed" 
                          size="small"
                          sx={{ 
                            ml: 2,
                            bgcolor: 'rgba(76, 175, 80, 0.1)', 
                            color: '#4CAF50' 
                          }}
                        />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={5} sm={3}>
                    <Box display="flex" justifyContent="flex-end">
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddSubtopic(topic.id);
                        }}
                        sx={{
                          mr: 1,
                          visibility: { xs: 'hidden', sm: 'visible' }
                        }}
                      >
                        Add Subtopic
                      </Button>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, 'topic', topic.id)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, py: 2 }}>
                <Box mb={2}>
                  <Typography variant="body1" paragraph>
                    {topic.description}
                  </Typography>
                  
                  {topic.preknowledge && (
                    <Box display="flex" alignItems="flex-start" mb={1}>
                      <PrerequisiteIcon sx={{ color: 'text.secondary', mr: 1, mt: 0.3 }} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight="medium">
                          Pre-knowledge:
                        </Typography>
                        <Typography variant="body2">
                          {topic.preknowledge}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {topic.skills && (
                    <Box display="flex" alignItems="flex-start" mb={1}>
                      <SkillIcon sx={{ color: 'text.secondary', mr: 1, mt: 0.3 }} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight="medium">
                          Required Skills:
                        </Typography>
                        <Typography variant="body2">
                          {topic.skills}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {topic.relatedTopics && topic.relatedTopics.length > 0 && (
                    <Box display="flex" alignItems="flex-start">
                      <LinkIcon sx={{ color: 'text.secondary', mr: 1, mt: 0.3 }} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight="medium">
                          Related Topics:
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          {topic.relatedTopics.map(relatedTopic => (
                            <Chip
                              key={relatedTopic.id}
                              label={relatedTopic.name}
                              size="small"
                              sx={{ mr: 1, mb: 1 }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
                
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                  Subtopics
                </Typography>
                
                {subtopics[topic.id] && subtopics[topic.id].length > 0 ? (
                  <List disablePadding>
                    {subtopics[topic.id].map((subtopic) => (
                      <ListItem 
                        key={subtopic.id}
                        disablePadding
                        sx={{ mb: 1 }}
                        secondaryAction={
                          <Box>
                            {!subtopic.completed && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleMarkSubtopicCompleted(topic.id, subtopic.id)}
                                sx={{
                                  mr: 1,
                                  borderColor: '#4CAF50',
                                  color: '#4CAF50',
                                  '&:hover': {
                                    borderColor: '#388E3C',
                                    bgcolor: 'rgba(76, 175, 80, 0.04)'
                                  }
                                }}
                              >
                                Mark Completed
                              </Button>
                            )}
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, 'subtopic', subtopic.id)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Box>
                        }
                      >
                        <ListItemButton 
                          sx={{ 
                            borderRadius: 1,
                            bgcolor: subtopic.completed ? 'rgba(76, 175, 80, 0.08)' : 'transparent'
                          }}
                        >
                          <ListItemIcon>
                            <SubtopicIcon sx={{ 
                              color: subtopic.completed ? '#4CAF50' : 'text.secondary' 
                            }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center">
                                <Typography fontWeight="medium">
                                  {subtopic.name}
                                </Typography>
                                {subtopic.completed && (
                                  <Chip
                                    label="Completed"
                                    size="small"
                                    icon={<CheckIcon />}
                                    sx={{
                                      ml: 2,
                                      bgcolor: 'rgba(76, 175, 80, 0.1)',
                                      color: '#4CAF50'
                                    }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={subtopic.description}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box textAlign="center" py={2}>
                    <Typography color="text.secondary">
                      No subtopics available for this topic
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddSubtopic(topic.id)}
                      sx={{ mt: 1 }}
                    >
                      Add Subtopic
                    </Button>
                  </Box>
                )}
                
                {!topic.completed && (
                  <Box mt={2} display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      onClick={() => handleMarkTopicCompleted(topic.id)}
                      sx={{
                        bgcolor: '#4CAF50',
                        '&:hover': { bgcolor: '#388E3C' }
                      }}
                    >
                      Mark Topic as Completed
                    </Button>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Paper>
      
      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {contextMenuType === 'topic' ? (
          <>
            <MenuItem onClick={handleEditTopic}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit Topic</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              handleAddSubtopic(selectedItemId);
              handleMenuClose();
            }}>
              <ListItemIcon>
                <AddIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Add Subtopic</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              handleMarkTopicCompleted(selectedItemId);
              handleMenuClose();
            }}>
              <ListItemIcon>
                <CheckIcon fontSize="small" sx={{ color: '#4CAF50' }} />
              </ListItemIcon>
              <ListItemText>Mark as Completed</ListItemText>
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem onClick={handleEditSubtopic}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit Subtopic</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <CheckIcon fontSize="small" sx={{ color: '#4CAF50' }} />
              </ListItemIcon>
              <ListItemText>Mark as Completed</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
      
      {/* Forms */}
      <TopicForm
        open={isTopicFormOpen}
        onClose={handleTopicFormClose}
        onSubmit={handleTopicFormSubmit}
        initialData={selectedTopic}
        subjectId={subject.id}
        userId={userId}
      />
      
      <SubtopicForm
        open={isSubtopicFormOpen}
        onClose={handleSubtopicFormClose}
        onSubmit={handleSubtopicFormSubmit}
        initialData={selectedSubtopic}
        topic={selectedTopic}
        subjectId={subject.id}
        userId={userId}
      />
    </Box>
  );
};

export default SubjectDetails;